import {
  FlyController,
  InputFrame,
  KeyboardMouseSource,
  MultiTouchSource,
  OrbitController,
  Pose,
  PROJECTION_PERSPECTIVE,
  Vec2,
  Vec3,
  math
} from 'playcanvas'

const frame = new InputFrame({
  move: [0, 0, 0],
  rotate: [0, 0, 0]
})
const tempMove = new Vec3()
const tempScreenMove = new Vec3()
const tempVector = new Vec3()
const tempFlyFocus = new Vec3()
const DEFAULT_PINCH_SPEED = 0.8
const MAX_PINCH_SPEED = 5
const MINIAPP_PINCH_INPUT_SCALE = 0.5625
const DEFAULT_WHEEL_SPEED = 0.04
const MIN_ZOOM_DISTANCE_RATIO = 0.08
const UNRESTRICTED_ZOOM_DISTANCE_EPSILON = 1e-6
const TWO_FINGER_GESTURE_THRESHOLD = 4
const TWO_FINGER_GESTURE_DOMINANCE = 1.2
const STANDARD_AUTO_MOTION_TYPES = new Set(['orbit', 'ellipse', 'spiral', 'dolly', 'swing'])

function autoMotionPosition(type, focus, baseVector, angle) {
  const radians = -angle * math.DEG_TO_RAD
  let effectiveRadians = radians
  let radiusMultiplier = 1
  let scaleZ = 1

  if (type === 'ellipse') {
    scaleZ = 0.7
  } else if (type === 'spiral') {
    radiusMultiplier = 1 + 0.1 * Math.sin(radians)
  } else if (type === 'dolly') {
    radiusMultiplier = 1 + 0.3 * Math.sin(radians * 2)
  } else if (type === 'swing') {
    const effectiveAngle = angle + 30 * Math.sin(angle * math.DEG_TO_RAD)
    effectiveRadians = -effectiveAngle * math.DEG_TO_RAD
  }

  const cos = Math.cos(effectiveRadians)
  const sin = Math.sin(effectiveRadians)
  return new Vec3(
    (baseVector.x * cos + baseVector.z * sin) * radiusMultiplier,
    baseVector.y * (type === 'dolly' ? radiusMultiplier : 1),
    (-baseVector.x * sin + baseVector.z * cos) * scaleZ * radiusMultiplier
  ).add(focus)
}

function screenToWorld(camera, canvas, dx, dy, distance, out = new Vec3()) {
  const { fov, aspectRatio, horizontalFov, projection, orthoHeight } = camera
  const rect = camera.system?.app?.graphicsDevice?.clientRect || canvas.getBoundingClientRect()
  // Pointer deltas are measured in the canvas' local CSS coordinate system.
  // clientWidth/clientHeight stay unrotated when software landscape applies a
  // CSS transform, while getBoundingClientRect reports the swapped viewport.
  const width = Math.max(1, canvas.clientWidth || Number(rect.width) || 1)
  const height = Math.max(1, canvas.clientHeight || Number(rect.height) || 1)

  out.set(-(dx / width) * 2, (dy / height) * 2, 0)
  if (projection === PROJECTION_PERSPECTIVE) {
    const halfSlice = distance * Math.tan(0.5 * fov * math.DEG_TO_RAD)
    out.x *= horizontalFov ? halfSlice : halfSlice * aspectRatio
    out.y *= horizontalFov ? halfSlice / aspectRatio : halfSlice
  } else {
    out.x *= orthoHeight * aspectRatio
    out.y *= orthoHeight
  }
  return out
}

function vectorFrom(value) {
  if (value?.clone) return value.clone()
  return new Vec3(Number(value?.x) || 0, Number(value?.y) || 0, Number(value?.z) || 0)
}

function finiteVectorFrom(value) {
  if (!value) return null
  const vector = vectorFrom(value)
  return [vector.x, vector.y, vector.z].every(Number.isFinite) ? vector : null
}

function touchPoint(touch) {
  return {
    x: Number(touch?.clientX) || 0,
    y: Number(touch?.clientY) || 0
  }
}

function touchGeometry(touches) {
  const count = Math.max(0, Number(touches?.length) || 0)
  if (!count) return null

  const first = touchPoint(touches.item?.(0) || touches[0])
  if (count === 1) return { count, x: first.x, y: first.y, distance: 0 }

  const second = touchPoint(touches.item?.(1) || touches[1])
  return {
    count,
    x: (first.x + second.x) * 0.5,
    y: (first.y + second.y) * 0.5,
    distance: Math.hypot(first.x - second.x, first.y - second.y)
  }
}

/**
 * The viewer interaction layer mirrors 3dgs_web's orbit and mobile fly paths:
 * orbit uses the stock mouse/multi-touch sources, while fly mode combines
 * a movement joystick with direct canvas dragging for camera look. Landscape
 * can additionally provide a second look joystick without disabling dragging.
 */
export class OrbitControls {
  constructor(canvas, cameraEntity) {
    this.canvas = canvas
    this.cameraEntity = cameraEntity
    this.camera = cameraEntity.camera
    this.desktopInput = new KeyboardMouseSource()
    this.mobileInput = new MultiTouchSource()
    this.orbitController = new OrbitController()
    this.flyController = new FlyController()
    this.controller = this.orbitController
    this.mode = 'orbit'
    this.softwareLandscape = false
    this.pose = new Pose().look(cameraEntity.getPosition(), Vec3.ZERO)
    this.mouseButtons = [0, 0, 0]
    this.mouseButtonMask = 0
    this.mouseModifierPan = false
    this.trackpadPanDelta = [0, 0]
    this.trackpadPanActive = false
    this.keys = new Vec3()
    this.touchCount = 0
    this.twoFingerGestureActive = false
    this.twoFingerGestureMode = ''
    this.twoFingerPanIntent = 0
    this.twoFingerZoomIntent = 0
    this.nativeTouchObserved = false
    this.nativeTouchCount = 0
    this.nativeTouchDelta = [0, 0]
    this.nativePinchDelta = 0
    this.nativeTouchReference = null
    this.smoothedMouse = [0, 0]
    this.smoothedTouch = [0, 0]
    this.smoothedJoystickLook = [0, 0]
    this.inputSmoothing = 0.08
    this.inputReleaseSmoothing = 0.35
    this.inputSmoothingThreshold = 0.05
    this.orbitSpeed = 24
    this.pinchSpeed = DEFAULT_PINCH_SPEED
    this.wheelSpeed = DEFAULT_WHEEL_SPEED
    this.autoRotateSpeed = 45
    this.autoRotateMotionType = 'orbit'
    this.autoRotateAngle = 0
    this.autoRotateFocus = null
    this.autoRotateBaseVector = null
    this.moveSpeed = 10
    this.mobileFlyMoveSpeedScale = 0.45
    this.mobileLookJoystickSensitivity = 0.45
    this.mobileLookJoystickSpeedScale = 6
    this.cameraResetDuration = 0.35
    this.enabled = true
    this.onInteraction = null
    this.onAutoRotateChange = null
    this.transition = null
    this._autoRotate = false
    this.boundsRadius = 1
    this.defaultOrbitPitchRange = new Vec2(-90, 5)
    this.defaultFlyPitchRange = new Vec2(-89, 89)
    this.freeOrbit = false
    this.movementBounds = null
    this.joystickInput = {
      left: [0, 0],
      right: [0, 0]
    }

    this.handlePointerButtons = this.handlePointerButtons.bind(this)
    this.handleNativeTouch = this.handleNativeTouch.bind(this)
    this.handleWheelGesture = this.handleWheelGesture.bind(this)

    this.orbitController.zoomRange = new Vec2(0, Infinity)
    this.orbitController.pitchRange = this.defaultOrbitPitchRange.clone()
    this.orbitController.rotateDamping = 0.98
    this.orbitController.moveDamping = 0.98
    this.orbitController.zoomDamping = 0.99
    this.flyController.pitchRange = this.defaultFlyPitchRange.clone()
    this.flyController.rotateDamping = 0.97
    this.flyController.moveDamping = 0.97
    this.controller.attach(this.pose, false)
    this.desktopInput.attach(canvas)
    this.mobileInput.attach(canvas)
    this.canvas.addEventListener('pointerdown', this.handlePointerButtons, true)
    this.canvas.addEventListener('pointermove', this.handlePointerButtons, true)
    this.canvas.addEventListener('pointerup', this.handlePointerButtons, true)
    this.canvas.addEventListener('pointercancel', this.handlePointerButtons, true)
    this.canvas.addEventListener('lostpointercapture', this.handlePointerButtons, true)
    // PlayCanvas' MultiTouchSource only listens for Pointer Events. XWeb and
    // older WKWebView builds can expose touch events without a complete
    // multi-pointer stream, so keep a native-touch path for mini-programs.
    this.canvas.addEventListener('touchstart', this.handleNativeTouch, { capture: true, passive: true })
    this.canvas.addEventListener('touchmove', this.handleNativeTouch, { capture: true, passive: true })
    this.canvas.addEventListener('touchend', this.handleNativeTouch, { capture: true, passive: true })
    this.canvas.addEventListener('touchcancel', this.handleNativeTouch, { capture: true, passive: true })
    this.canvas.addEventListener('wheel', this.handleWheelGesture, { capture: true, passive: true })
    this.applyPose()
  }

  handlePointerButtons(event) {
    if (event.pointerType !== 'mouse') return
    this.mouseButtonMask = Math.max(0, Number(event.buttons) || 0)
    this.mouseModifierPan = Boolean((this.mouseButtonMask & 1) && (event.shiftKey || event.altKey))
  }

  handleWheelGesture(event) {
    const deltaX = Number(event.deltaX) || 0
    const deltaY = Number(event.deltaY) || 0
    const pixelMode = Number(event.deltaMode) === 0
    // The WeChat desktop simulator cannot create two simultaneous contacts.
    // A precision touchpad arrives as small pixel-mode wheel deltas instead.
    // Ctrl+wheel is a pinch-to-zoom signal and a large vertical delta is a
    // regular mouse wheel, so keep both on the existing zoom path.
    const looksLikeTrackpadPan = !event.ctrlKey && pixelMode && (
      Math.abs(deltaX) > 0.01 || (Math.abs(deltaY) > 0.01 && Math.abs(deltaY) < 50)
    )
    if (!looksLikeTrackpadPan) return

    // Wheel deltas describe content scrolling, which is opposite to the
    // direction the fingers travel across a touchpad.
    this.trackpadPanDelta[0] -= deltaX
    this.trackpadPanDelta[1] -= deltaY
    this.trackpadPanActive = true
  }

  handleNativeTouch(event) {
    const touches = event.targetTouches || event.touches
    if (!touches) return

    this.nativeTouchObserved = true
    const next = touchGeometry(touches)
    const previous = this.nativeTouchReference
    const nextCount = next?.count || 0

    // A finger entering or leaving changes the centroid abruptly. Rebase the
    // gesture on that event instead of treating it as camera movement.
    if (next && previous?.count === next.count) {
      this.nativeTouchDelta[0] += next.x - previous.x
      this.nativeTouchDelta[1] += next.y - previous.y
      if (next.count > 1) this.nativePinchDelta += previous.distance - next.distance
    }

    this.nativeTouchCount = nextCount
    this.nativeTouchReference = next
  }

  readTouchInput(mobile) {
    if (this.nativeTouchObserved) {
      const previousCount = this.touchCount
      this.touchCount = this.nativeTouchCount
      const input = {
        touch: [...this.nativeTouchDelta],
        pinch: [this.nativePinchDelta],
        count: [this.touchCount - previousCount]
      }
      this.nativeTouchDelta.fill(0)
      this.nativePinchDelta = 0
      return input
    }

    const trackedTouchCount = this.mobileInput?._pointerEvents?.size
    this.touchCount = Number.isFinite(trackedTouchCount)
      ? trackedTouchCount
      : Math.max(0, this.touchCount + (mobile.count[0] || 0))
    return mobile
  }

  resolveTwoFingerGesture(active, touch = [0, 0], pinch = [0]) {
    if (!active) {
      this.twoFingerGestureMode = ''
      this.twoFingerPanIntent = 0
      this.twoFingerZoomIntent = 0
      return ''
    }
    if (this.twoFingerGestureMode) return this.twoFingerGestureMode

    this.twoFingerPanIntent += Math.hypot(Number(touch[0]) || 0, Number(touch[1]) || 0)
    this.twoFingerZoomIntent += Math.abs(Number(pinch[0]) || 0)
    const pan = this.twoFingerPanIntent
    const zoom = this.twoFingerZoomIntent
    const strongest = Math.max(pan, zoom)
    if (strongest < TWO_FINGER_GESTURE_THRESHOLD) return ''

    if (zoom >= pan * TWO_FINGER_GESTURE_DOMINANCE) this.twoFingerGestureMode = 'zoom'
    else if (pan >= zoom * TWO_FINGER_GESTURE_DOMINANCE) this.twoFingerGestureMode = 'pan'
    else if (strongest >= TWO_FINGER_GESTURE_THRESHOLD * 2) {
      this.twoFingerGestureMode = zoom > pan ? 'zoom' : 'pan'
    }
    return this.twoFingerGestureMode
  }

  get autoRotate() {
    return this._autoRotate
  }

  set autoRotate(value) {
    const next = this.mode === 'orbit' && Boolean(value)
    if (next === this._autoRotate) return
    if (next) {
      this.clearTransientInput()
      this.orbitController.attach(this.pose, false)
      this.captureAutoRotateBase()
    } else {
      this.autoRotateFocus = null
      this.autoRotateBaseVector = null
    }
    this._autoRotate = next
    this.onAutoRotateChange?.(next)
  }

  setAutoRotateMotionType(value) {
    const normalized = String(value || '').trim()
    this.autoRotateMotionType = STANDARD_AUTO_MOTION_TYPES.has(normalized) ? normalized : 'orbit'
    if (this.autoRotate) this.captureAutoRotateBase()
    return this.autoRotateMotionType
  }

  captureAutoRotateBase() {
    const focus = this.getCurrentFocusPoint()
    const vector = this.pose.position.clone().sub(focus)
    if (vector.lengthSq() < 1e-8) return false
    this.autoRotateAngle = 0
    this.autoRotateFocus = focus
    this.autoRotateBaseVector = vector
    return true
  }

  get distance() {
    return Math.max(0, Number(this.pose.distance) || 0)
  }

  get rotateSpeed() {
    return this.orbitSpeed
  }

  set rotateSpeed(value) {
    const next = Number(value)
    if (Number.isFinite(next)) this.orbitSpeed = next
  }

  get pinchSpeed() {
    return this._pinchSpeed
  }

  set pinchSpeed(value) {
    const next = Number(value)
    this._pinchSpeed = math.clamp(
      Number.isFinite(next) ? next : DEFAULT_PINCH_SPEED,
      0.1,
      MAX_PINCH_SPEED
    )
  }

  getCurrentFocusPoint() {
    if (this.transition?.currentFocus) return this.transition.currentFocus.clone()
    return this.pose.getFocus(new Vec3())
  }

  setBounds(radius, referenceDistance = this.distance, options = {}) {
    this.boundsRadius = Math.max(Number(radius) || 1, 0.05)
    const distance = Math.max(Number(referenceDistance) || this.boundsRadius, 0.05)
    const nearClip = Math.max(Number(this.camera.nearClip) || 0.005, 0.001)
    const minDistance = options.unrestrictedZoom
      ? UNRESTRICTED_ZOOM_DISTANCE_EPSILON
      : Math.max(nearClip * 2, distance * MIN_ZOOM_DISTANCE_RATIO)
    const zoomRange = new Vec2(minDistance, Infinity)
    this.orbitController.zoomRange = zoomRange
    return zoomRange
  }

  normalizeOrbitPitchRange(range) {
    if (!range) return null
    const min = Number(range.x ?? range.min ?? range[0])
    const max = Number(range.y ?? range.max ?? range[1])
    if (![min, max].every(Number.isFinite) || max < min) return null
    return new Vec2(
      math.clamp(min, -89, 89),
      math.clamp(max, -89, 89)
    )
  }

  restoreMovementPitchRanges() {
    this.orbitController.pitchRange = this.freeOrbit
      ? new Vec2(-Infinity, Infinity)
      : this.defaultOrbitPitchRange.clone()
    this.flyController.pitchRange = this.defaultFlyPitchRange.clone()
  }

  setFreeOrbit(enabled) {
    this.freeOrbit = Boolean(enabled)
    this.movementBounds = null
    if (this.freeOrbit) this.orbitController.zoomRange = new Vec2(0, Infinity)
    this.restoreMovementPitchRanges()
    return this.freeOrbit
  }

  setMovementBounds(bounds) {
    if (bounds?.mode !== 'sphere-window') {
      this.clearMovementBounds()
      return false
    }

    const center = finiteVectorFrom(bounds.center)
    const forward = finiteVectorFrom(bounds.forward)
    const right = finiteVectorFrom(bounds.right)
    const up = finiteVectorFrom(bounds.up)
    const minRadius = Number(bounds.minRadius)
    const maxRadius = Number(bounds.maxRadius)
    const minYaw = Number(bounds.minYaw)
    const maxYaw = Number(bounds.maxYaw)
    const minPitch = Number(bounds.minPitch)
    const maxPitch = Number(bounds.maxPitch)
    const constrainRadius = bounds.constrainRadius !== false
    const lockOrbitFocus = Boolean(bounds.lockOrbitFocus)
    const limitFlyOrientation = Boolean(bounds.limitFlyOrientation)
    const orbitPitchRange = this.normalizeOrbitPitchRange(bounds.orbitPitchRange)
    if (
      !center || !forward || !right || !up ||
      ![minRadius, maxRadius, minYaw, maxYaw, minPitch, maxPitch].every(Number.isFinite) ||
      forward.lengthSq() < 1e-12 || right.lengthSq() < 1e-12 || up.lengthSq() < 1e-12 ||
      minRadius < 0 || maxRadius <= 0 || maxRadius < minRadius ||
      maxYaw < minYaw || maxPitch < minPitch
    ) {
      this.clearMovementBounds()
      return false
    }

    const softMargin = Number(bounds.softMargin)
    const pullStrength = Number(bounds.pullStrength)
    this.movementBounds = {
      mode: 'sphere-window',
      center,
      forward: forward.normalize(),
      right: right.normalize(),
      up: up.normalize(),
      minRadius,
      maxRadius,
      minYaw,
      maxYaw,
      minPitch,
      maxPitch,
      constrainRadius,
      lockOrbitFocus,
      limitFlyOrientation,
      orbitPitchRange: orbitPitchRange?.clone() || null,
      softMargin: Number.isFinite(softMargin) && softMargin > 0 ? softMargin : 0.5,
      pullStrength: Number.isFinite(pullStrength) && pullStrength > 0 ? pullStrength : 8,
      sampleCount: Math.max(0, Number(bounds.sampleCount) || 0),
      fullYawCoverage: Boolean(bounds.fullYawCoverage)
    }
    this.orbitController.pitchRange = (orbitPitchRange || new Vec2(-89, 89)).clone()
    this.flyController.pitchRange = this.defaultFlyPitchRange.clone()
    this.enforceMovementBounds({ ignorePitch: false })
    return true
  }

  clearMovementBounds() {
    this.movementBounds = null
    this.restoreMovementPitchRanges()
  }

  getMovementBounds() {
    if (!this.movementBounds) return null
    return {
      ...this.movementBounds,
      center: this.movementBounds.center.clone(),
      forward: this.movementBounds.forward.clone(),
      right: this.movementBounds.right.clone(),
      up: this.movementBounds.up.clone(),
      orbitPitchRange: this.movementBounds.orbitPitchRange?.clone() || null
    }
  }

  getPose() {
    return {
      focus: this.getCurrentFocusPoint(),
      position: this.pose.position.clone()
    }
  }

  clampToMovementBounds(position, options = {}) {
    const bounds = this.movementBounds
    if (bounds?.mode !== 'sphere-window' || !position) return null

    const {
      center, forward, right, up, minRadius, maxRadius,
      minYaw, maxYaw, minPitch, maxPitch, constrainRadius
    } = bounds
    const offsetX = position.x - center.x
    const offsetY = position.y - center.y
    const offsetZ = position.z - center.z
    const distance = Math.hypot(offsetX, offsetY, offsetZ)
    // Some pipelines only constrain the captured viewing angles. Their zoom
    // remains free apart from the controller's near-clip safety distance.
    const clampedDistance = constrainRadius === false
      ? distance
      : math.clamp(distance, minRadius, maxRadius)
    const inverseDistance = distance > 1e-6 ? 1 / distance : 0
    const directionX = distance > 1e-6 ? offsetX * inverseDistance : forward.x
    const directionY = distance > 1e-6 ? offsetY * inverseDistance : forward.y
    const directionZ = distance > 1e-6 ? offsetZ * inverseDistance : forward.z

    let yaw = Math.atan2(
      directionX * right.x + directionY * right.y + directionZ * right.z,
      directionX * forward.x + directionY * forward.y + directionZ * forward.z
    )
    const yawCenter = (minYaw + maxYaw) * 0.5
    while (yaw - yawCenter > Math.PI) yaw -= Math.PI * 2
    while (yaw - yawCenter < -Math.PI) yaw += Math.PI * 2

    const pitch = Math.asin(math.clamp(
      directionX * up.x + directionY * up.y + directionZ * up.z,
      -1,
      1
    ))
    const clampedYaw = math.clamp(yaw, minYaw, maxYaw)
    const clampedPitch = options.ignorePitch ? pitch : math.clamp(pitch, minPitch, maxPitch)
    if (
      Math.abs(clampedDistance - distance) <= 1e-6 &&
      Math.abs(clampedYaw - yaw) <= 1e-8 &&
      Math.abs(clampedPitch - pitch) <= 1e-8
    ) return null

    const cosPitch = Math.cos(clampedPitch)
    const yawSinCosPitch = Math.sin(clampedYaw) * cosPitch
    const pitchSin = Math.sin(clampedPitch)
    const yawCosCosPitch = Math.cos(clampedYaw) * cosPitch
    let clampedDirectionX = right.x * yawSinCosPitch + up.x * pitchSin + forward.x * yawCosCosPitch
    let clampedDirectionY = right.y * yawSinCosPitch + up.y * pitchSin + forward.y * yawCosCosPitch
    let clampedDirectionZ = right.z * yawSinCosPitch + up.z * pitchSin + forward.z * yawCosCosPitch
    const clampedDirectionLength = Math.hypot(
      clampedDirectionX,
      clampedDirectionY,
      clampedDirectionZ
    )
    if (clampedDirectionLength < 1e-6) {
      clampedDirectionX = forward.x
      clampedDirectionY = forward.y
      clampedDirectionZ = forward.z
    } else {
      const inverseClampedDirectionLength = 1 / clampedDirectionLength
      clampedDirectionX *= inverseClampedDirectionLength
      clampedDirectionY *= inverseClampedDirectionLength
      clampedDirectionZ *= inverseClampedDirectionLength
    }

    return new Vec3(
      center.x + clampedDirectionX * clampedDistance,
      center.y + clampedDirectionY * clampedDistance,
      center.z + clampedDirectionZ * clampedDistance
    )
  }

  enforceMovementBounds(options = {}) {
    if (this.mode === 'fly' && this.movementBounds?.limitFlyOrientation) {
      return this.enforceFlyOrientationBounds(options)
    }
    const clampedPosition = this.clampToMovementBounds(this.pose.position, options)
    const bounds = this.movementBounds
    const lockOrbitFocus = this.mode === 'orbit' && bounds?.lockOrbitFocus
    const currentFocus = lockOrbitFocus ? this.getCurrentFocusPoint() : null
    const focusOffsetSq = currentFocus
      ? (currentFocus.x - bounds.center.x) ** 2 +
        (currentFocus.y - bounds.center.y) ** 2 +
        (currentFocus.z - bounds.center.z) ** 2
      : 0
    if (!clampedPosition && (!lockOrbitFocus || focusOffsetSq <= 1e-10)) return false

    if (this.mode === 'orbit') {
      const focus = lockOrbitFocus
        ? bounds.center
        : (options.focus ? vectorFrom(options.focus) : this.getCurrentFocusPoint())
      this.pose.look(clampedPosition || this.pose.position, focus)
    } else {
      // A fly boundary limits where the camera can go, not where it can look.
      this.pose.position.copy(clampedPosition)
    }
    this.controller.attach(this.pose, false)
    if (options.apply !== false) this.applyPose()
    return true
  }

  enforceFlyOrientationBounds(options = {}) {
    const bounds = this.movementBounds
    if (bounds?.mode !== 'sphere-window') return false

    const { forward, right, up, minYaw, maxYaw, minPitch, maxPitch } = bounds
    const lookTarget = this.pose.getFocus(tempFlyFocus)
    let radialX = this.pose.position.x - lookTarget.x
    let radialY = this.pose.position.y - lookTarget.y
    let radialZ = this.pose.position.z - lookTarget.z
    const radialLength = Math.hypot(radialX, radialY, radialZ)
    if (radialLength < 1e-8) return false
    radialX /= radialLength
    radialY /= radialLength
    radialZ /= radialLength

    let yaw = Math.atan2(
      radialX * right.x + radialY * right.y + radialZ * right.z,
      radialX * forward.x + radialY * forward.y + radialZ * forward.z
    )
    const yawCenter = (minYaw + maxYaw) * 0.5
    while (yaw - yawCenter > Math.PI) yaw -= Math.PI * 2
    while (yaw - yawCenter < -Math.PI) yaw += Math.PI * 2
    const pitch = Math.asin(math.clamp(
      radialX * up.x + radialY * up.y + radialZ * up.z,
      -1,
      1
    ))
    const clampedYaw = math.clamp(yaw, minYaw, maxYaw)
    const clampedPitch = options.ignorePitch ? pitch : math.clamp(pitch, minPitch, maxPitch)
    if (
      Math.abs(clampedYaw - yaw) <= 1e-8 &&
      Math.abs(clampedPitch - pitch) <= 1e-8
    ) return false

    const cosPitch = Math.cos(clampedPitch)
    const yawSinCosPitch = Math.sin(clampedYaw) * cosPitch
    const pitchSin = Math.sin(clampedPitch)
    const yawCosCosPitch = Math.cos(clampedYaw) * cosPitch
    const clampedRadial = new Vec3(
      right.x * yawSinCosPitch + up.x * pitchSin + forward.x * yawCosCosPitch,
      right.y * yawSinCosPitch + up.y * pitchSin + forward.y * yawCosCosPitch,
      right.z * yawSinCosPitch + up.z * pitchSin + forward.z * yawCosCosPitch
    ).normalize()
    const lookDistance = Math.max(Math.abs(Number(this.pose.distance) || 0), 1)
    lookTarget.copy(this.pose.position).sub(clampedRadial.mulScalar(lookDistance))
    this.pose.look(this.pose.position, lookTarget)
    this.controller.attach(this.pose, false)
    if (options.apply !== false) this.applyPose()
    return true
  }

  setMode(value) {
    const next = value === 'fly' ? 'fly' : 'orbit'
    if (next === this.mode) return this.mode

    const focus = this.getCurrentFocusPoint()
    this.transition = null
    this.autoRotate = false
    this.clearTransientInput()
    this.setJoystickInput([0, 0], [0, 0])
    this.controller.detach()
    this.mode = next
    if (next === 'orbit') this.pose.look(this.pose.position, focus)
    this.controller = next === 'fly' ? this.flyController : this.orbitController
    this.controller.attach(this.pose, false)
    this.enforceMovementBounds({ ignorePitch: false, focus, apply: false })
    this.applyPose()
    return this.mode
  }

  setJoystickInput(left = [0, 0], right = [0, 0]) {
    const clampAxis = value => math.clamp(Number(value) || 0, -1, 1)
    this.joystickInput.left[0] = clampAxis(left[0])
    this.joystickInput.left[1] = clampAxis(left[1])
    this.joystickInput.right[0] = clampAxis(right[0])
    this.joystickInput.right[1] = clampAxis(right[1])
  }

  setSoftwareLandscape(active) {
    const next = Boolean(active)
    if (next === this.softwareLandscape) return
    this.softwareLandscape = next
    this.clearTransientInput()
  }

  setPose(focus, position, options = {}) {
    return this.reset(focus, position, { immediate: true, ...options })
  }

  focusOnEntity(entity, direction = null) {
    const gsplat = entity?.gsplat
    const resource = gsplat?.resource ?? gsplat?._assetReference?.asset?.resource
    const aabb = resource?.octree && resource.aabb?.center && resource.aabb?.halfExtents
      ? resource.aabb
      : gsplat?.instance?.meshInstance?.aabb
    if (!aabb?.center || !aabb?.halfExtents) return null

    const focus = aabb.center.clone()
    const halfExtents = aabb.halfExtents.clone()
    const radius = Math.max(halfExtents.x, halfExtents.y, halfExtents.z, 0.05)
    const fitDistance = radius / Math.sin(0.5 * this.camera.fov * math.DEG_TO_RAD)
    const padding = matchMedia('(pointer: coarse)').matches ? 1.8 : 1
    const lookDirection = direction ? vectorFrom(direction) : new Vec3(0, 0, 1)
    lookDirection.y = 0
    if (lookDirection.lengthSq() < 1e-8) lookDirection.set(0, 0, 1)
    lookDirection.normalize()
    const position = focus.clone()
      .add(lookDirection.mulScalar(fitDistance * padding))
      .add(new Vec3(0, halfExtents.y, 0))

    this.setBounds(Math.max(halfExtents.length(), radius), position.distance(focus))
    return this.reset(focus, position)
  }

  reset(focus, position, options = {}) {
    const nextFocus = vectorFrom(focus)
    const nextPosition = vectorFrom(position)
    const constrain = options.constrain !== false
    const ignorePitch = options.ignorePitch ?? false
    if (constrain) {
      const clampedPosition = this.clampToMovementBounds(nextPosition, { ignorePitch })
      if (clampedPosition) nextPosition.copy(clampedPosition)
    }
    const targetPose = new Pose().look(nextPosition, nextFocus)
    const duration = Number.isFinite(options.duration)
      ? Math.max(0, options.duration)
      : this.cameraResetDuration

    if (options.immediate || duration <= 0) {
      this.transition = null
      this.pose.copy(targetPose)
      this.controller.attach(this.pose, false)
      this.applyPose()
      return { focus: nextFocus, cameraPosition: nextPosition }
    }

    this.transition = {
      elapsed: 0,
      duration,
      startPosition: this.pose.position.clone(),
      endPosition: targetPose.position.clone(),
      startFocus: this.getCurrentFocusPoint(),
      endFocus: nextFocus.clone(),
      currentFocus: this.getCurrentFocusPoint(),
      targetPose,
      constrain,
      ignorePitch
    }
    this.clearTransientInput()
    return { focus: nextFocus, cameraPosition: nextPosition }
  }

  lookFrom(direction, distance = this.distance, options = {}) {
    const focus = this.getCurrentFocusPoint()
    const normalized = vectorFrom(direction)
    if (normalized.lengthSq() < 1e-8) normalized.set(0, 0, 1)
    normalized.normalize()
    const position = normalized.mulScalar(Math.max(Number(distance) || 0, 0.0001)).add(focus)
    return this.reset(focus, position, options)
  }

  applyPose() {
    this.cameraEntity.setPosition(this.pose.position)
    this.cameraEntity.setEulerAngles(this.pose.angles)
  }

  clearTransientInput() {
    this.desktopInput?.read?.()
    this.mobileInput?.read?.()
    frame.read()
    this.mouseButtons.fill(0)
    this.mouseButtonMask = 0
    this.mouseModifierPan = false
    this.trackpadPanDelta.fill(0)
    this.trackpadPanActive = false
    this.keys.set(0, 0, 0)
    this.touchCount = 0
    this.twoFingerGestureActive = false
    this.twoFingerGestureMode = ''
    this.twoFingerPanIntent = 0
    this.twoFingerZoomIntent = 0
    this.nativeTouchDelta.fill(0)
    this.nativePinchDelta = 0
    this.smoothedMouse.fill(0)
    this.smoothedTouch.fill(0)
    this.smoothedJoystickLook.fill(0)
  }

  markInteraction() {
    if (this.autoRotate) this.autoRotate = false
    this.onInteraction?.()
  }

  smoothDelta(previous, current, dt, threshold = this.inputSmoothingThreshold) {
    const smoothing = math.clamp(Number(this.inputSmoothing) || 0.08, 0, 1)
    const release = math.clamp(Number(this.inputReleaseSmoothing) || 0.35, 0, 1)
    const safeDt = math.clamp(Number(dt) || 1 / 60, 0, 0.1)
    const releaseRetention = Math.pow(1 - release, safeDt * 60)
    const next = Math.abs(current) > 1e-6
      ? previous * (1 - smoothing) + current * smoothing
      : previous * releaseRetention
    return Math.abs(next) < threshold ? 0 : next
  }

  updateTransition(dt) {
    const transition = this.transition
    transition.elapsed = Math.min(transition.elapsed + dt, transition.duration)
    let progress = transition.duration > 0 ? transition.elapsed / transition.duration : 1
    progress = progress * progress * (3 - 2 * progress)
    this.pose.position.lerp(transition.startPosition, transition.endPosition, progress)
    transition.currentFocus.lerp(transition.startFocus, transition.endFocus, progress)
    this.pose.look(this.pose.position, transition.currentFocus)
    if (transition.constrain) {
      const clampedPosition = this.clampToMovementBounds(this.pose.position, {
        ignorePitch: transition.ignorePitch
      })
      if (clampedPosition) this.pose.look(clampedPosition, transition.currentFocus)
    }

    if (progress >= 1) {
      this.pose.copy(transition.targetPose)
      if (transition.constrain) {
        const clampedPosition = this.clampToMovementBounds(this.pose.position, {
          ignorePitch: transition.ignorePitch
        })
        if (clampedPosition) this.pose.look(clampedPosition, transition.endFocus)
      }
      this.controller.attach(this.pose, false)
      this.transition = null
    }
    this.applyPose()
  }

  updateAutoRotate(dt) {
    if (!this.autoRotateFocus || !this.autoRotateBaseVector) {
      if (!this.captureAutoRotateBase()) return
    }
    this.autoRotateAngle = (this.autoRotateAngle + this.autoRotateSpeed * dt) % 360
    const position = autoMotionPosition(
      this.autoRotateMotionType,
      this.autoRotateFocus,
      this.autoRotateBaseVector,
      this.autoRotateAngle
    )
    this.pose.look(position, this.autoRotateFocus)
    this.enforceMovementBounds({ ignorePitch: true, focus: this.autoRotateFocus, apply: false })
    this.controller.attach(this.pose, false)
    this.applyPose()
  }

  updateFly(dt, desktop, mobile) {
    const keyCode = KeyboardMouseSource.keyCode
    const { key, button, mouse, wheel } = desktop
    const { touch, pinch } = mobile
    const { left, right } = this.joystickInput
    this.trackpadPanDelta.fill(0)
    this.trackpadPanActive = false
    // The software landscape fallback rotates the whole viewer 90 degrees.
    // Remap direct canvas drags back into the viewer coordinate system while
    // leaving the virtual joysticks (which handle landscape themselves) alone.
    const lookMouse = this.softwareLandscape
      ? [mouse[1], -mouse[0]]
      : mouse
    const twoFingerActive = this.touchCount > 1
    const lookTouch = twoFingerActive
      ? [0, 0]
      : (this.softwareLandscape ? [touch[1], -touch[0]] : touch)
    this.mouseButtons.forEach((value, index) => {
      this.mouseButtons[index] = math.clamp(value + (button[index] || 0), 0, 1)
    })
    this.keys.add(tempVector.set(
      (key[keyCode.D] - key[keyCode.A]) + (key[keyCode.RIGHT] - key[keyCode.LEFT]),
      key[keyCode.E] - key[keyCode.Q],
      (key[keyCode.W] - key[keyCode.S]) + (key[keyCode.UP] - key[keyCode.DOWN])
    ))

    const moving = this.keys.lengthSq() > 1e-8 || Math.abs(left[0]) > 1e-4 || Math.abs(left[1]) > 1e-4 || Math.abs(wheel[0]) > 1e-4
    const looking = mouse.some(Boolean) || lookTouch.some(Boolean) || Math.abs(right[0]) > 1e-4 || Math.abs(right[1]) > 1e-4
    if (moving || looking) this.markInteraction()

    const baseMoveSpeed = Math.max(0.1, Number(this.moveSpeed) || 10)
    const keyMove = tempMove.copy(this.keys)
    if (keyMove.lengthSq() > 1) keyMove.normalize()
    frame.deltas.move.append([
      (keyMove.x * baseMoveSpeed + left[0] * baseMoveSpeed * this.mobileFlyMoveSpeedScale) * dt,
      keyMove.y * baseMoveSpeed * dt,
      (keyMove.z * baseMoveSpeed + left[1] * baseMoveSpeed * this.mobileFlyMoveSpeedScale - wheel[0] * this.wheelSpeed +
        // MultiTouchSource reports previous distance minus current distance;
        // negate it here so fingers moving apart move the fly camera forward,
        // matching the usual pinch-to-zoom direction.
        (twoFingerActive ? -(Number(pinch[0]) || 0) * this.pinchSpeed * MINIAPP_PINCH_INPUT_SCALE : 0)) * dt
    ])

    this.smoothedMouse[0] = this.smoothDelta(this.smoothedMouse[0], lookMouse[0], dt)
    this.smoothedMouse[1] = this.smoothDelta(this.smoothedMouse[1], lookMouse[1], dt)
    this.smoothedTouch[0] = this.smoothDelta(this.smoothedTouch[0], lookTouch[0], dt)
    this.smoothedTouch[1] = this.smoothDelta(this.smoothedTouch[1], lookTouch[1], dt)
    // Joystick axes are normalized, unlike pixel deltas; keep fine deflections
    // while applying the same response and release smoothing as canvas drags.
    this.smoothedJoystickLook[0] = this.smoothDelta(this.smoothedJoystickLook[0], right[0], dt, 1e-4)
    this.smoothedJoystickLook[1] = this.smoothDelta(this.smoothedJoystickLook[1], right[1], dt, 1e-4)
    const lookSpeed = Math.max(0.1, Number(this.mobileLookJoystickSensitivity) || 0.45) *
      Math.max(1, Number(this.mobileLookJoystickSpeedScale) || 6) *
      Math.max(1, Number(this.orbitSpeed) || 24)
    frame.deltas.rotate.append([
      (this.smoothedMouse[0] + this.smoothedTouch[0]) * this.orbitSpeed * dt + this.smoothedJoystickLook[0] * lookSpeed * dt,
      (this.smoothedMouse[1] + this.smoothedTouch[1]) * this.orbitSpeed * dt - this.smoothedJoystickLook[1] * lookSpeed * dt,
      0
    ])

    this.pose.copy(this.flyController.update(frame, dt))
    this.enforceMovementBounds({ ignorePitch: false, apply: false })
    this.applyPose()
  }

  update(dt) {
    const safeDt = math.clamp(Number(dt) || 1 / 60, 0, 0.1)

    if (!this.enabled) {
      this.clearTransientInput()
      return
    }

    const desktop = this.desktopInput.read()
    const mobile = this.readTouchInput(this.mobileInput.read())

    if (this.transition) {
      frame.read()
      this.mouseButtons.fill(0)
      this.touchCount = 0
      this.keys.set(0, 0, 0)
      this.smoothedMouse.fill(0)
      this.smoothedTouch.fill(0)
      this.smoothedJoystickLook.fill(0)
      this.updateTransition(safeDt)
      return
    }

    if (this.mode === 'fly') {
      this.updateFly(safeDt, desktop, mobile)
      return
    }

    const { button, mouse, wheel } = desktop
    const { touch, pinch, count } = mobile
    // Keep orbit gestures aligned with the direction the finger moves on the
    // screen. Remapping these deltas for the CSS-rotated landscape fallback
    // swaps pitch and yaw in the embedded WebView.

    this.mouseButtons.forEach((value, index) => {
      this.mouseButtons[index] = math.clamp(value + (button[index] || 0), 0, 1)
    })
    const hasInput = Boolean(
      wheel[0] || button.some(Boolean) || mouse.some(Boolean) ||
      count[0] || touch.some(Boolean) || pinch[0]
    )
    if (hasInput) this.markInteraction()

    const twoFingerActive = this.touchCount > 1
    if (twoFingerActive !== this.twoFingerGestureActive) {
      this.smoothedTouch.fill(0)
      this.twoFingerGestureActive = twoFingerActive
    }
    const twoFinger = Number(twoFingerActive)
    const twoFingerGesture = this.resolveTwoFingerGesture(twoFingerActive, touch, pinch)
    const twoFingerPan = Number(twoFingerGesture === 'pan')
    const twoFingerZoom = Number(twoFingerGesture === 'zoom')
    const mousePan = Boolean(
      this.mouseButtons[1] || this.mouseButtons[2] ||
      button[1] === -1 || button[2] === -1 ||
      (this.mouseButtonMask & 6) || this.mouseModifierPan
    )
    const trackpadPanActive = this.trackpadPanActive
    const trackpadPan = [...this.trackpadPanDelta]
    this.trackpadPanDelta.fill(0)
    this.trackpadPanActive = false
    const pan = Number(mousePan || twoFingerPan || trackpadPanActive)
    const distance = Math.max(this.pose.distance, 0.0001)

    // The software landscape fallback rotates the viewer 90 degrees with CSS,
    // so canvas drag deltas must be mapped back into landscape coordinates.
    const orbitMouse = this.softwareLandscape
      ? [mouse[1], -mouse[0]]
      : mouse
    const orbitTouch = this.softwareLandscape
      ? [touch[1], -touch[0]]
      : touch

    const mousePanDelta = mousePan
      ? orbitMouse
      : [0, 0]
    const trackpadPanDelta = this.softwareLandscape
      ? [trackpadPan[1], -trackpadPan[0]]
      : trackpadPan
    tempMove.set(0, 0, 0)
    screenToWorld(
      this.camera,
      this.canvas,
      mousePanDelta[0] + trackpadPanDelta[0],
      mousePanDelta[1] + trackpadPanDelta[1],
      distance,
      tempScreenMove
    )
    tempMove.add(tempScreenMove)
    tempMove.z -= wheel[0] * Number(!trackpadPanActive) * this.wheelSpeed * safeDt
    frame.deltas.move.append([tempMove.x, tempMove.y, -tempMove.z])

    if (pan) {
      this.smoothedMouse.fill(0)
    } else {
      this.smoothedMouse[0] = this.smoothDelta(this.smoothedMouse[0], orbitMouse[0], safeDt)
      this.smoothedMouse[1] = this.smoothDelta(this.smoothedMouse[1], orbitMouse[1], safeDt)
    }
    frame.deltas.rotate.append([
      this.smoothedMouse[0] * (1 - pan) * this.orbitSpeed * safeDt,
      this.smoothedMouse[1] * (1 - pan) * this.orbitSpeed * safeDt,
      0
    ])

    tempMove.set(0, 0, 0)
    screenToWorld(this.camera, this.canvas, orbitTouch[0], orbitTouch[1], distance, tempScreenMove)
    tempMove.add(tempScreenMove.mulScalar(twoFingerPan))
    tempMove.z += pinch[0] * twoFingerZoom * this.pinchSpeed * MINIAPP_PINCH_INPUT_SCALE * safeDt
    frame.deltas.move.append([tempMove.x, tempMove.y, tempMove.z])

    this.smoothedTouch[0] = this.smoothDelta(this.smoothedTouch[0], orbitTouch[0], safeDt)
    this.smoothedTouch[1] = this.smoothDelta(this.smoothedTouch[1], orbitTouch[1], safeDt)
    frame.deltas.rotate.append([
      this.smoothedTouch[0] * (1 - twoFinger) * this.orbitSpeed * safeDt,
      this.smoothedTouch[1] * (1 - twoFinger) * this.orbitSpeed * safeDt,
      0
    ])

    this.pose.copy(this.controller.update(frame, safeDt))
    this.enforceMovementBounds({ ignorePitch: true, apply: false })
    this.applyPose()
    if (this.autoRotate && !hasInput) this.updateAutoRotate(safeDt)
  }

  destroy() {
    this.canvas.removeEventListener('pointerdown', this.handlePointerButtons, true)
    this.canvas.removeEventListener('pointermove', this.handlePointerButtons, true)
    this.canvas.removeEventListener('pointerup', this.handlePointerButtons, true)
    this.canvas.removeEventListener('pointercancel', this.handlePointerButtons, true)
    this.canvas.removeEventListener('lostpointercapture', this.handlePointerButtons, true)
    this.canvas.removeEventListener('touchstart', this.handleNativeTouch, true)
    this.canvas.removeEventListener('touchmove', this.handleNativeTouch, true)
    this.canvas.removeEventListener('touchend', this.handleNativeTouch, true)
    this.canvas.removeEventListener('touchcancel', this.handleNativeTouch, true)
    this.canvas.removeEventListener('wheel', this.handleWheelGesture, true)
    this.desktopInput.destroy()
    this.mobileInput.destroy()
    this.orbitController.destroy()
    this.flyController.destroy()
    this.setJoystickInput([0, 0], [0, 0])
    this.transition = null
  }
}
